"use client";

import { useId, useMemo, useState } from "react";
import { useLocale } from "next-intl";

type UnitCategory = "length" | "weight" | "temperature" | "volume" | "area" | "speed";

type UnitDefinition = {
  label: string;
  value: string;
  toBase: number;
};

const UNIT_CATEGORIES: Record<
  UnitCategory,
  { label: string; units: UnitDefinition[]; hint: string }
> = {
  length: {
    label: "Length",
    hint: "Common uses include measuring desks, spaces, travel distances, and project plans.",
    units: [
      { label: "Meters", value: "meters", toBase: 1 },
      { label: "Kilometers", value: "kilometers", toBase: 1000 },
      { label: "Miles", value: "miles", toBase: 1609.344 },
      { label: "Feet", value: "feet", toBase: 0.3048 },
      { label: "Inches", value: "inches", toBase: 0.0254 },
      { label: "Centimeters", value: "centimeters", toBase: 0.01 },
    ],
  },
  weight: {
    label: "Weight",
    hint: "Useful for cooking, shipping, fitness tracking, and ingredient scaling.",
    units: [
      { label: "Kilograms", value: "kilograms", toBase: 1 },
      { label: "Grams", value: "grams", toBase: 0.001 },
      { label: "Pounds", value: "pounds", toBase: 0.45359237 },
      { label: "Ounces", value: "ounces", toBase: 0.028349523125 },
      { label: "Metric tons", value: "metric-tons", toBase: 1000 },
    ],
  },
  temperature: {
    label: "Temperature",
    hint: "Use °F = (°C × 9/5) + 32 or °C = (°F − 32) × 5/9 for quick manual checks.",
    units: [
      { label: "Celsius", value: "celsius", toBase: 1 },
      { label: "Fahrenheit", value: "fahrenheit", toBase: 1 },
      { label: "Kelvin", value: "kelvin", toBase: 1 },
    ],
  },
  volume: {
    label: "Volume",
    hint: "Helpful for recipes, storage planning, lab work, and fuel or liquid measurements.",
    units: [
      { label: "Liters", value: "liters", toBase: 1 },
      { label: "Milliliters", value: "milliliters", toBase: 0.001 },
      { label: "Gallons", value: "gallons", toBase: 3.785411784 },
      { label: "Cups", value: "cups", toBase: 0.2365882365 },
      { label: "Fluid ounces", value: "fluid-ounces", toBase: 0.0295735295625 },
    ],
  },
  area: {
    label: "Area",
    hint: "Great for floor plans, lot sizes, real estate, and garden planning.",
    units: [
      { label: "Square meters", value: "sq-meters", toBase: 1 },
      { label: "Square kilometers", value: "sq-kilometers", toBase: 1000000 },
      { label: "Square feet", value: "sq-feet", toBase: 0.09290304 },
      { label: "Square miles", value: "sq-miles", toBase: 2589988.110336 },
      { label: "Acres", value: "acres", toBase: 4046.8564224 },
      { label: "Hectares", value: "hectares", toBase: 10000 },
    ],
  },
  speed: {
    label: "Speed",
    hint: "Useful for road trips, running splits, wind speeds, and travel times.",
    units: [
      { label: "Kilometers per hour", value: "kmh", toBase: 1 },
      { label: "Miles per hour", value: "mph", toBase: 1.609344 },
      { label: "Meters per second", value: "mps", toBase: 3.6 },
      { label: "Knots", value: "knots", toBase: 1.852 },
    ],
  },
};

const ABSOLUTE_ZERO = { celsius: -273.15, fahrenheit: -459.67, kelvin: 0 };

function celsiusFromTemperature(fromUnit: string, value: number) {
  if (fromUnit === "fahrenheit") return (value - 32) * (5 / 9);
  if (fromUnit === "kelvin") return value - 273.15;
  return value;
}

function fahrenheitFromTemperature(fromUnit: string, value: number) {
  if (fromUnit === "celsius") return value * (9 / 5) + 32;
  if (fromUnit === "kelvin") return (value - 273.15) * (9 / 5) + 32;
  return value;
}

function kelvinFromTemperature(fromUnit: string, value: number) {
  if (fromUnit === "celsius") return value + 273.15;
  if (fromUnit === "fahrenheit") return (value - 32) * (5 / 9) + 273.15;
  return value;
}

  /** Formats with thousands separators and adaptive precision, respecting active locale. */
  function formatUnitNumber(value: number, locale?: string): string {
    if (!Number.isFinite(value)) return "0";
    if (value === 0) return "0";

    const abs = Math.abs(value);

    // Very small nonzero numbers: use enough decimals to show a real digit,
    // never round down to a misleading "0".
    if (abs < 0.000001) {
      return value.toExponential(4);
    }
    if (abs < 1) {
      return trimTrailingZeros(value.toFixed(6));
    }

    // Larger numbers: thousands separators, up to 4 decimal places.
    const rounded = Number(value.toFixed(4));
    return rounded.toLocaleString(locale, { maximumFractionDigits: 4 });
  }

  function trimTrailingZeros(text: string) {
    return text.includes(".") ? text.replace(/0+$/, "").replace(/\.$/, "") : text;
  }

  function convertUnitValue(category: UnitCategory, fromUnit: string, toUnit: string, value: number) {
    const fromDefinition = UNIT_CATEGORIES[category].units.find((unit) => unit.value === fromUnit);
    const toDefinition = UNIT_CATEGORIES[category].units.find((unit) => unit.value === toUnit);
    if (!fromDefinition || !toDefinition) return null;

    if (category === "temperature") {
      const converted =
        toUnit === "celsius"
          ? celsiusFromTemperature(fromUnit, value)
          : toUnit === "fahrenheit"
            ? fahrenheitFromTemperature(fromUnit, value)
            : kelvinFromTemperature(fromUnit, value);
      return { number: converted, label: toDefinition.label };
    }

    const normalizedValue = value * fromDefinition.toBase;
    const converted = normalizedValue / toDefinition.toBase;
    return { number: converted, label: toDefinition.label };
  }

  function isBelowAbsoluteZero(unit: string, value: number) {
    if (unit === "celsius") return value < ABSOLUTE_ZERO.celsius;
    if (unit === "fahrenheit") return value < ABSOLUTE_ZERO.fahrenheit;
    if (unit === "kelvin") return value < ABSOLUTE_ZERO.kelvin;
    return false;
  }

  export default function UnitConverter({ }: { toolName: string }) {
    const locale = useLocale();
    const idPrefix = useId();

    // In metric locales (es, pt-BR, hi, id), default length converts meters to kilometers/centimeters
    const isMetricPreferred = locale !== "en";

    const [category, setCategory] = useState<UnitCategory>("length");
    const [rawInput, setRawInput] = useState("1");
    const [fromUnit, setFromUnit] = useState("meters");
    const [toUnit, setToUnit] = useState(isMetricPreferred ? "centimeters" : "feet");
    const [copyLabel, setCopyLabel] = useState("Copy result");

    const unitOptions = UNIT_CATEGORIES[category].units;

    const trimmedInput = rawInput.trim();
    const parsedValue = trimmedInput === "" ? null : Number.parseFloat(trimmedInput);
    const isValidNumber = trimmedInput !== "" && Number.isFinite(parsedValue);

    const belowAbsoluteZero =
      category === "temperature" && isValidNumber && isBelowAbsoluteZero(fromUnit, parsedValue as number);

    const result = useMemo(() => {
      if (!isValidNumber) return null;
      return convertUnitValue(category, fromUnit, toUnit, parsedValue as number);
    }, [category, fromUnit, toUnit, isValidNumber, parsedValue]);

    const resultText = result ? `${formatUnitNumber(result.number, locale)} ${result.label}` : "";

    function handleCategoryChange(nextCategory: UnitCategory) {
      const units = UNIT_CATEGORIES[nextCategory].units;

      setCategory(nextCategory);
      setFromUnit(units[0].value);
      setToUnit(units[1]?.value ?? units[0].value);
    }

    function swapUnits() {
      setFromUnit(toUnit);
      setToUnit(fromUnit);
    }

    async function copyResult() {
      if (!resultText) return;
      try {
        await navigator.clipboard.writeText(resultText);
        setCopyLabel("Copied!");
      } catch {
        setCopyLabel("Copy failed");
      }
      window.setTimeout(() => setCopyLabel("Copy result"), 1500);
    }

    return (
      <div className="space-y-4">
        <div className="flex flex-wrap gap-2" role="group" aria-label="Unit category">
          {(Object.keys(UNIT_CATEGORIES) as UnitCategory[]).map((key) => (
            <button
              type="button"
              key={key}
              aria-pressed={category === key}
              onClick={() => handleCategoryChange(key)}
              className={`rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${category === key
                  ? "bg-indigo-600 text-white"
                  : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                }`}
            >
              {UNIT_CATEGORIES[key].label}
            </button>
          ))}
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <label htmlFor={`${idPrefix}-from`} className="text-sm font-medium text-slate-700">
            From
            <select
              id={`${idPrefix}-from`}
              value={fromUnit}
              onChange={(event) => setFromUnit(event.target.value)}
              className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
            >
              {unitOptions.map((unit) => (
                <option key={unit.value} value={unit.value}>
                  {unit.label}
                </option>
              ))}
            </select>
          </label>

          <label htmlFor={`${idPrefix}-to`} className="text-sm font-medium text-slate-700">
            To
            <select
              id={`${idPrefix}-to`}
              value={toUnit}
              onChange={(event) => setToUnit(event.target.value)}
              className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
            >
              {unitOptions.map((unit) => (
                <option key={unit.value} value={unit.value}>
                  {unit.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="grid gap-4 md:grid-cols-[1fr_auto_1fr] md:items-end">
          <label htmlFor={`${idPrefix}-value`} className="text-sm font-medium text-slate-700">
            Value
            <input
              id={`${idPrefix}-value`}
              type="text"
              inputMode="decimal"
              value={rawInput}
              onChange={(event) => setRawInput(event.target.value)}
              aria-invalid={trimmedInput !== "" && !isValidNumber}
              className={`mt-2 w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2 ${trimmedInput !== "" && !isValidNumber
                  ? "border-rose-400 focus:ring-rose-200"
                  : "border-slate-300 focus:border-indigo-500 focus:ring-indigo-200"
                }`}
              placeholder="Enter a number"
            />
          </label>

          <button
            type="button"
            onClick={swapUnits}
            aria-label="Swap from and to units"
            className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-slate-800"
          >
            ⇅ Swap
          </button>

          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-900">
            <div className="flex items-center justify-between gap-2">
              <p className="font-semibold">Result</p>
              <button
                type="button"
                onClick={() => void copyResult()}
                disabled={!resultText}
                className="rounded-md bg-slate-900 px-2 py-1 text-xs font-semibold text-white transition-colors hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-300"
              >
                {copyLabel}
              </button>
            </div>
            <p aria-live="polite" className="mt-1 text-lg font-bold text-indigo-600">
              {resultText || "—"}
            </p>
          </div>
        </div>

        {trimmedInput !== "" && !isValidNumber && (
          <p role="alert" className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
            Enter a valid number to see a converted result.
          </p>
        )}
        {belowAbsoluteZero && (
          <p role="alert" className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            That&apos;s below absolute zero, so this isn&apos;t a physically possible temperature. The math still
            runs, but the result won&apos;t be meaningful.
          </p>
        )}

        <p className="text-sm text-slate-600">{UNIT_CATEGORIES[category].hint}</p>
      </div>
    );
  }
